/* ==========================================================================
   dg-account.js — behaviour for the customer account area

   Deliberately does NOT reuse the theme's own hooks. theme.js has a customerTemplates
   module that binds #RecoverPassword, #AddressNewForm, .address-new-toggle and friends;
   if these templates kept those ids both scripts would bind to the same elements and a
   click would toggle twice, i.e. do nothing. Every hook below is a data-dga-* attribute
   the theme has never heard of, and the ids the theme looks for are gone, so its module
   finds nothing and no-ops.

   What it drives:
     - the password-recovery panel on /account/login (including the #recover deep link
       the "Forgot your password?" link in checkout sends people to)
     - the add / edit address panels and the delete confirmation
     - the country -> province selects, via Shopify.CountryProvinceSelector, which
       theme.liquid loads (shopify_common.js, deferred) on customer templates only
     - the order-list search + status filter on /account
     - the print button on an order

   Everything degrades: with JS off the address panels render open (the template writes
   them collapsed only once this script has confirmed it is running), forms still submit,
   and the order list shows every row.
   ========================================================================== */

(function () {
  'use strict';

  // Runs on DOMContentLoaded rather than inline: shopify_common.js is loaded with defer
  // in the head, and deferred scripts finish executing just before that event. Binding
  // any earlier would mean Shopify.CountryProvinceSelector does not exist yet.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {

  var root = document.querySelector('[data-dga]');
  if (!root) return;

  /* ---------------------------------------------------------------- helpers */

  function all(selector, scope) {
    return Array.prototype.slice.call((scope || root).querySelectorAll(selector));
  }

  function show(el) { if (el) el.classList.remove('dga-hidden'); }
  function hide(el) { if (el) el.classList.add('dga-hidden'); }
  function isHidden(el) { return !el || el.classList.contains('dga-hidden'); }

  function focusFirstField(panel) {
    var field = panel.querySelector('input:not([type="hidden"]), select, textarea');
    if (field) {
      try { field.focus({ preventScroll: true }); } catch (e) { field.focus(); }
    }
  }

  /* --------------------------------------------------------- login / recover

     Two panels that swap. The sign-in panel is the default; #recover in the URL — which
     is where Shopify's own checkout "forgot password" link points — opens the other one.
  */

  (function loginPanels() {
    var signIn = root.querySelector('[data-dga-panel="signin"]');
    var recover = root.querySelector('[data-dga-panel="recover"]');
    if (!signIn || !recover) return;

    function toRecover(focus) {
      hide(signIn);
      show(recover);
      if (focus) focusFirstField(recover);
    }

    function toSignIn(focus) {
      hide(recover);
      show(signIn);
      if (focus) focusFirstField(signIn);
    }

    all('[data-dga-show="recover"]').forEach(function (btn) {
      btn.addEventListener('click', function (evt) {
        evt.preventDefault();
        toRecover(true);
        if (window.history && window.history.replaceState) {
          window.history.replaceState(null, '', '#recover');
        }
      });
    });

    all('[data-dga-show="signin"]').forEach(function (btn) {
      btn.addEventListener('click', function (evt) {
        evt.preventDefault();
        toSignIn(true);
        if (window.history && window.history.replaceState) {
          window.history.replaceState(null, '', window.location.pathname);
        }
      });
    });

    // Shopify re-renders the login page after a recovery submit; the template leaves a
    // marker so the confirmation is shown instead of the sign-in form.
    if (root.querySelector('[data-dga-recover-sent]')) {
      toRecover(false);
    } else if (window.location.hash === '#recover') {
      toRecover(false);
    }
  })();

  /* -------------------------------------------------------------- addresses */

  (function addresses() {
    var newPanel = root.querySelector('[data-dga-address-new]');
    var hasPanels = newPanel || root.querySelector('[data-dga-address-edit]');
    if (!hasPanels) return;

    // Collapse what the template rendered open. Doing it here rather than in the markup
    // is what keeps the page usable when this file fails to load.
    if (newPanel) hide(newPanel);
    all('[data-dga-address-edit]').forEach(hide);

    function closeAll(except) {
      if (newPanel && newPanel !== except) hide(newPanel);
      all('[data-dga-address-edit]').forEach(function (panel) {
        if (panel !== except) hide(panel);
      });
    }

    function toggle(panel, trigger) {
      if (!panel) return;
      var opening = isHidden(panel);
      closeAll(opening ? panel : null);
      if (opening) {
        show(panel);
        focusFirstField(panel);
        panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      } else {
        hide(panel);
        if (trigger) trigger.focus();
      }
      if (trigger) trigger.setAttribute('aria-expanded', opening ? 'true' : 'false');
    }

    all('[data-dga-address-add]').forEach(function (btn) {
      btn.setAttribute('aria-expanded', 'false');
      btn.addEventListener('click', function () { toggle(newPanel, btn); });
    });

    all('[data-dga-address-edit-toggle]').forEach(function (btn) {
      btn.setAttribute('aria-expanded', 'false');
      btn.addEventListener('click', function () {
        var id = btn.getAttribute('data-dga-address-edit-toggle');
        toggle(root.querySelector('[data-dga-address-edit="' + id + '"]'), btn);
      });
    });

    all('[data-dga-address-cancel]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var panel = btn.closest('[data-dga-address-new], [data-dga-address-edit]');
        hide(panel);
      });
    });

    all('[data-dga-address-delete]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var id = btn.getAttribute('data-dga-address-delete');
        var message = btn.getAttribute('data-dga-confirm') || 'Delete this address?';
        if (!window.confirm(message)) return;
        if (window.Shopify && window.Shopify.postLink) {
          window.Shopify.postLink('/account/addresses/' + id, { parameters: { _method: 'delete' } });
        }
      });
    });

    // Country -> province. shopify_common.js is deferred, so it has parsed by the time
    // DOMContentLoaded fires and this runs; the guard is for the case where it 404s.
    if (window.Shopify && window.Shopify.CountryProvinceSelector) {
      if (document.getElementById('AddressCountryNew')) {
        new window.Shopify.CountryProvinceSelector('AddressCountryNew', 'AddressProvinceNew', {
          hideElement: 'AddressProvinceContainerNew'
        });
      }
      all('[data-dga-country]').forEach(function (select) {
        var id = select.getAttribute('data-dga-country');
        new window.Shopify.CountryProvinceSelector('AddressCountry_' + id, 'AddressProvince_' + id, {
          hideElement: 'AddressProvinceContainer_' + id
        });
      });
    }

    // A failed submit re-renders the page with the errors inside one panel. Open it.
    var errored = root.querySelector('[data-dga-address-new] .dga-errors, [data-dga-address-edit] .dga-errors');
    if (errored) {
      var panel = errored.closest('[data-dga-address-new], [data-dga-address-edit]');
      show(panel);
      panel.scrollIntoView({ block: 'center' });
    }
  })();

  /* ------------------------------------------------------------- order list */

  (function orderFilter() {
    var list = root.querySelector('[data-dga-orders]');
    if (!list) return;

    var search = root.querySelector('[data-dga-order-search]');
    var status = root.querySelector('[data-dga-order-status]');
    var empty = root.querySelector('[data-dga-orders-empty]');
    var rows = all('[data-dga-order]', list);
    if (!rows.length) return;

    function apply() {
      var query = (search && search.value || '').toLowerCase().trim();
      var want = status && status.value || 'all';
      var shown = 0;

      rows.forEach(function (row) {
        var haystack = (row.getAttribute('data-search') || '').toLowerCase();
        var state = row.getAttribute('data-status') || '';
        var match = (!query || haystack.indexOf(query) !== -1) && (want === 'all' || state === want);
        row.hidden = !match;
        if (match) shown++;
      });

      if (empty) empty.hidden = shown !== 0;
    }

    if (search) search.addEventListener('input', apply);
    if (status) status.addEventListener('change', apply);
  })();

  /* ------------------------------------------------------------------ print */

  all('[data-dga-print]').forEach(function (btn) {
    btn.addEventListener('click', function () { window.print(); });
  });

  }
})();
